// addonIndicatorUtils — shared decision logic for add-on visual indicators
// in staff-facing surfaces (StaffDashboard Guest badge, SuiteCalendar event titles).
//
// Introduced by STAFF-ADDON-VISIBILITY-001 (hotfix, 2026-06-15) to extend
// the existing Guest-badge condition for corp-tier $15 double-occupancy
// bookings (ADR-042 D1, shipped in CORP-DOUBLE-OCC-001 / PR #213,
// 2026-06-08) that were previously invisible in staff surfaces.
//
// CFG=N/A — read-only visual indicator decision; no money path flips.

/**
 * Resolve which Guest-badge variant to render for an infrared booking.
 *
 * Background: prior to PR #213, the only "guest" flavour was the
 * $25 guest-pass / day-pass path tracked via `booking.addGuest`. PR #213
 * added `booking.doubleOccupancy: boolean` for the corp-tier $15 add-on
 * (ADR-042 D1) but the StaffDashboard Guest badge was not updated, so
 * corp-double-occupancy bookings rendered without any guest indicator.
 *
 * Per ADR-042 D1, `addGuest` and `doubleOccupancy` are mutually
 * exclusive at the BE policy layer. This helper returns `'both'`
 * defensively for the invariant-violation case so callers can both
 * render a safe fallback and log a warning (R-024-class signal).
 *
 * @param {object} booking - booking record (snake-typed legacy shape)
 * @returns {'none' | 'guest' | 'guestCorp' | 'both'}
 */
export function resolveGuestBadgeVariant(booking) {
  if (!booking) return "none";
  const addGuest = Boolean(booking.addGuest);
  const doubleOccupancy = Boolean(booking.doubleOccupancy);
  if (addGuest && doubleOccupancy) return "both";
  if (doubleOccupancy) return "guestCorp";
  if (addGuest) return "guest";
  return "none";
}

/**
 * Return true if the booking carries ANY add-on flag that should surface
 * a visual indicator on a compact surface (e.g., a calendar event title).
 *
 * Add-ons considered: addGuest, doubleOccupancy, aromatherapy, halotherapy.
 * Resilient to missing fields — the SuiteCalendar BE projection does not
 * currently include these fields, so the predicate degrades to false
 * gracefully until Simone's BE projection-extension PR deploys.
 *
 * @param {object} booking - booking record (snake-typed legacy shape)
 * @returns {boolean}
 */
export function hasAnyAddon(booking) {
  if (!booking) return false;
  return Boolean(
    booking.addGuest ||
      booking.doubleOccupancy ||
      booking.aromatherapy ||
      booking.halotherapy
  );
}
